import React from 'react';
import styles from './BetTableAB20.module.css';

const BetTableAB20 = ({ data = [], exposures = {}, gameData = {} }) => {
  // Map card names to numbers (A=1, 2=2, ..., K=13)
  const cardNameToNumber = {
    'A': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
    '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13
  };

  // Parse ares and bres to determine which positions have cards
  const ares = gameData?.data?.data?.ares || gameData?.ares || "";
  const bres = gameData?.data?.data?.bres || gameData?.bres || "";
  const aall = gameData?.data?.data?.aall || gameData?.aall || "";
  const ball = gameData?.data?.data?.ball || gameData?.ball || "";

  // Parse ares: "0,0,0,0,0,6,0,8,9,10,0,12,13" -> array where index is position-1, value is order
  const aresArray = ares ? ares.split(',').map(v => parseInt(v.trim()) || 0) : [];
  // Parse bres: "21,22,0,24,25,0,0,0,0,0,31,0,0" -> array where index is position-21, value is order
  const bresArray = bres ? bres.split(',').map(v => parseInt(v.trim()) || 0) : [];
  
  // Parse aall and ball card codes
  const aallCards = aall ? aall.split(',').map(c => c.trim()) : [];
  const ballCards = ball ? ball.split(',').map(c => c.trim()) : [];

  // Create mapping: position (sid) -> card code
  // aresArray values are just flags (non-zero means card exists)
  // Cards are assigned sequentially based on order in aall
  const andarCardMap = {};
  let aallIndex = 0;
  aresArray.forEach((flag, index) => {
    if (flag > 0 && aallIndex < aallCards.length) {
      const position = index + 1; // sid 1-13
      andarCardMap[position] = aallCards[aallIndex];
      aallIndex++;
    }
  });

  // bresArray values are just flags (non-zero means card exists)
  // Cards are assigned sequentially based on order in ball
  const baharCardMap = {};
  let ballIndex = 0;
  bresArray.forEach((flag, index) => {
    if (flag > 0 && ballIndex < ballCards.length) {
      const position = 21 + index; // sid 21-33
      baharCardMap[position] = ballCards[ballIndex];
      ballIndex++;
    }
  });

  // Extract Andar cards (sid 1-13)
  const andarCards = [];
  for (let i = 1; i <= 13; i++) {
    const cardData = data.find(item => item.sid === i) || {};
    const nat = cardData.nat || '';
    const cardName = nat.replace('Andar ', '').trim();
    const cardNumber = cardNameToNumber[cardName] || 0;
    // Check if this position has a card from ares
    const hasCard = aresArray[i - 1] > 0;
    const cardCode = andarCardMap[i] || null;
    
    andarCards.push({
      ...cardData,
      cardNumber,
      cardName,
      hasCard,
      cardCode
    });
  }

  // Extract Bahar cards (sid 21-33)
  const baharCards = [];
  for (let i = 21; i <= 33; i++) {
    const cardData = data.find(item => item.sid === i) || {};
    const nat = cardData.nat || '';
    const cardName = nat.replace('Bahar ', '').trim();
    // Check if this position has a card from bres
    const bresIndex = i - 21;
    const hasCard = bresArray[bresIndex] > 0;
    const cardCode = baharCardMap[i] || null;
    
    baharCards.push({
      ...cardData,
      cardName,
      hasCard,
      cardCode,
      sid: i
    });
  }

  // Check if bet is suspended
  const isSuspended = (betData) => {
    if (!betData) return true;
    const value = betData?.b ?? 0;
    const gstatus = betData?.gstatus?.toUpperCase();
    return value === 0 || value === "0" || value === undefined || value === null || gstatus === "SUSPENDED";
  };

  // Get card image URL
  // Show card image if hasCard is true and cardCode exists, otherwise show patti_back.jpg
  const getCardImageUrl = (cardData, isBahar = false) => {
    // If card has been drawn (hasCard) and has a cardCode, show the card
    if (cardData?.hasCard && cardData?.cardCode) {
      // Parse card code to get card number (e.g., "QSS" -> Q=12, "6CC" -> 6)
      const cardCode = cardData.cardCode;
      // Extract card rank from code (first part before suit)
      let cardNum = 0;
      
      // Handle face cards
      if (cardCode.startsWith('A')) {
        cardNum = 1;
      } else if (cardCode.startsWith('J')) {
        cardNum = 11;
      } else if (cardCode.startsWith('Q')) {
        cardNum = 12;
      } else if (cardCode.startsWith('K')) {
        cardNum = 13;
      } else {
        // Extract number (e.g., "6CC" -> 6, "10DD" -> 10)
        const match = cardCode.match(/^(\d+)/);
        if (match) {
          cardNum = parseInt(match[1]);
        }
      }
      
      if (cardNum > 0 && cardNum <= 13) {
        return `/assets/img/card/${cardNum}.jpg`;
      }
    }
    // Otherwise show patti_back.jpg
    return `/assets/img/card/patti_back.jpg`;
  };

  // Get exposure value
  const getExposure = (selection) => {
    if (!selection) return 0;
    const exposureKeys = [
      selection,
      selection?.toLowerCase(),
      selection?.trim()
    ];

    for (const key of exposureKeys) {
      if (key && exposures[key] !== undefined) {
        return parseFloat(exposures[key]) || 0;
      }
    }
    return 0;
  };

  // Format exposure
  const formatExposure = (exposure) => {
    if (!exposure || exposure === 0) return "0";
    return exposure.toFixed(2);
  };

  return (
    <div className={styles.container}>
      {/* Payout Text */}
      <div className="text-right">
        <span className="box-10 remark">Payout : Bahar 1st Card 25% and All Other Andar-Bahar Cards 100%.</span>
      </div>
      
      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive m-t-5">
            <table className="table table-bordered">
              <tbody>
                {/* Andar Row */}
                <tr style={{backgroundColor: '#ffa07a', background: '#ffa07a'}} className="andar-bg">
                  <td className="box-1 player-name">
                    <b>Andar</b>
                  </td>
                  <td style={{backgroundColor: '#ffa07a', background: '#ffa07a'}} className="box-9 text-center andarbahartd">
                    {andarCards.map((card, index) => {
                      const exposure = getExposure(card.nat || card.cardName);
                      const suspended = isSuspended(card);
                      
                      return (
                        <span key={`andar-${card.sid || index}`} className={styles.cardWrapper}>
                          <img 
                            src={getCardImageUrl(card, false)} 
                            alt={card.cardName || `Card ${card.cardNumber}`}
                            className={`andar-bahar-image ${suspended ? 'suspended' : ''}`}
                            onError={(e) => {
                              e.target.src = `/assets/img/card/patti_back.jpg`;
                            }}
                          />
                          <span className={styles.exposureValue} style={{ color: 'black' }}>{formatExposure(exposure)}</span>
                        </span>
                      );
                    })}
                  </td>
                </tr>
                
                {/* Bahar Row */}
                <tr style={{backgroundColor: '#90ee90', background: '#90ee90'}} className="bahar-bg">
                  <td className="box-1 player-name">
                    <b>Bahar</b>
                  </td>
                  <td style={{backgroundColor: '#90ee90', background: '#90ee90'}} className="box-9 text-center andarbahartd">
                    {baharCards.map((card, index) => {
                      const exposure = getExposure(card.nat || card.cardName);
                      const suspended = isSuspended(card);
                      
                      return (
                        <span key={`bahar-${card.sid || index}`} className={styles.cardWrapper}>
                          <img 
                            src={getCardImageUrl(card, true)} 
                            alt={card.cardName || `Card ${card.cardNumber}`}
                            className={`andar-bahar-image ${suspended ? 'suspended' : ''}`}
                            onError={(e) => {
                              e.target.src = `/assets/img/card/patti_back.jpg`;
                            }}
                          />
                          <span className={styles.exposureValue} style={{ color: 'black' }}>{formatExposure(exposure)}</span>
                        </span>
                      );
                    })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetTableAB20;

